/**
 * STRICT Central Capability Permission Hook
 *
 * `can(action)` checks Central capabilities ONLY:
 *   workspace.capabilities['levelup-legacy'][domain][action] === true
 *
 * NO role-based fallback.  Missing capability data → false.
 *
 * NOTE: Server enforces via capability middleware — this is UX gating.
 */

import React, { useMemo } from 'react';
import type {
  CentralRole,
  CentralContext,
  WorkspacePermissionKey,
} from '@levelup/central-context';
import {
  hasMinimumRole,
  hasRole as sharedHasRole,
  hasCapability as sharedHasCapability,
  hasWorkspacePermission as sharedHasWorkspacePermission,
} from '@levelup/central-context';
import { useBootContext } from './useBootContext';
import { getLegacyTestPersona } from '../lib/centralTestingMode';
import { LEGACY_PERSONAS } from '@legacy/shared';
import type { LegacyPersona } from '@legacy/shared';

/**
 * Aliased from shared @levelup/central-context CentralRole for local use.
 */
type Role = CentralRole;

const APP_ID = 'levelup-legacy';

/**
 * Role-based capability fallback used ONLY when Central has sent no
 * capability data for this app. Mirrors the persona bundles in
 * packages/shared/src/types/personas.ts:
 *   owner/admin → full write
 *   member      → Legacy Operator bundle (broad member writes, no settings.write)
 *   viewer      → read-only
 *
 * The frontend is optimistic here; the backend's requirePermission middleware
 * is authoritative and will 403 if Central's claims don't match.
 */
const ROLE_FALLBACK: Record<Role, Record<string, Record<string, boolean>>> = {
  owner: {
    workspace: { read: true },
    agents: { read: true, write: true },
    chat: { read: true, write: true },
    plans: { read: true, write: true },
    recommendations: { read: true, write: true },
    alerts: { read: true, write: true },
    settings: { read: true, write: true },
    snapshots: { read: true, write: true },
  },
  admin: {
    workspace: { read: true },
    agents: { read: true, write: true },
    chat: { read: true, write: true },
    plans: { read: true, write: true },
    recommendations: { read: true, write: true },
    alerts: { read: true, write: true },
    settings: { read: true, write: true },
    snapshots: { read: true, write: true },
  },
  member: {
    workspace: { read: true },
    agents: { read: true, write: true },
    chat: { read: true, write: true },
    plans: { read: true, write: true },
    recommendations: { read: true, write: true },
    alerts: { read: true, write: true },
    settings: { read: true, write: false },
    snapshots: { read: true, write: true },
  },
  viewer: {
    workspace: { read: true },
    agents: { read: true, write: false },
    chat: { read: true, write: false },
    plans: { read: true, write: false },
    recommendations: { read: true, write: false },
    alerts: { read: true, write: false },
    settings: { read: true, write: false },
    snapshots: { read: true, write: false },
  },
};

export interface UsePermissionsResult {
  /** User's current role */
  role: Role | undefined;

  /** Check capability from Central: workspace.capabilities['levelup-legacy'][domain][action] */
  can: (action: string) => boolean;

  /**
   * "Soft" capability check: returns true unless Central has explicitly
   * denied the action (false). When capability data is missing or the action
   * is absent from the map (null), returns true.
   *
   * Use for UX-only gating like hiding menu items. Never use for security
   * decisions — those must go through can()/hasCapability().
   */
  mayView: (action: string) => boolean;

  /** Check if user has one of the allowed roles (delegates to shared) */
  hasRole: (...roles: Role[]) => boolean;

  /** Check if user is at least the given role level (delegates to shared) */
  isAtLeast: (role: Role) => boolean;

  /** Check if user has a capability (delegates to shared) */
  hasCapability: (domain: string, action: string) => boolean;

  /** Check if user has a workspace permission (delegates to shared) */
  hasWorkspacePermission: (permission: WorkspacePermissionKey) => boolean;

  /** Whether user is a viewer (read-only) */
  isViewer: boolean;

  /** Whether user is admin or owner */
  isAdmin: boolean;

  /** Whether user is the owner */
  isOwner: boolean;
}

/**
 * Builds a CentralContext from boot state for use with shared pure functions.
 */
function buildCentralContext(bootContext: {
  currentWorkspace: { role: string; capabilities?: Record<string, Record<string, Record<string, boolean>>>; permissions?: Record<string, boolean> };
  apps: Array<{ appId: string }>;
  meta?: { features?: { capabilities?: boolean; workspacePermissions?: boolean } } | null;
} | null): CentralContext | null {
  if (!bootContext) return null;
  return {
    currentWorkspace: {
      role: bootContext.currentWorkspace.role,
      capabilities: bootContext.currentWorkspace.capabilities,
      permissions: bootContext.currentWorkspace.permissions as CentralContext['currentWorkspace']['permissions'],
    },
    apps: bootContext.apps.map((a) => ({ appId: a.appId })),
    meta: bootContext.meta ? { features: bootContext.meta.features } : null,
  };
}

/**
 * Hook for checking user permissions.
 *
 * @example
 * ```tsx
 * const { can, isViewer, hasCapability, hasWorkspacePermission } = usePermissions();
 *
 * if (can('agents:write')) {
 *   // Show run button
 * }
 *
 * if (hasWorkspacePermission('manageBilling')) {
 *   // Show billing settings
 * }
 * ```
 */
export function usePermissions(): UsePermissionsResult {
  const bootContext = useBootContext();
  const role = bootContext?.currentWorkspace.role as Role | undefined;

  const centralCtx = useMemo(
    () => buildCentralContext(bootContext),
    [bootContext],
  );

  const can = (action: string): boolean => {
    // Parse 'domain:action' format
    const sep = action.indexOf(':');
    if (sep === -1) return false;
    const domain = action.slice(0, sep);
    const act = action.slice(sep + 1);

    // 1. Testing-mode in-app persona takes precedence whenever one is active.
    //    Applies regardless of the currently-reported Central role because
    //    Central's switch-context may not yet reflect the override (especially
    //    in staging). The persona's expected bundle is the WHOLE answer for
    //    the session — we intentionally don't consult Central's capabilities
    //    map while a test persona is set.
    const persona = getLegacyTestPersona() as LegacyPersona | null;
    if (persona) {
      const def = LEGACY_PERSONAS.find((p) => p.persona === persona);
      const bundle = def?.expectedCapabilities as
        | Record<string, Record<string, boolean>>
        | undefined;
      return bundle?.[domain]?.[act] === true;
    }

    // 2. Central capabilities are authoritative when present.
    const appCaps = centralCtx?.currentWorkspace?.capabilities?.[APP_ID];
    if (appCaps) {
      const explicit = appCaps[domain]?.[act];
      if (explicit === true) return true;
      if (explicit === false) return false;
      // missing key → fall through
    }

    // 3. Role fallback when no persona and no Central data.
    //    Backend's requirePermission is the authoritative check — this
    //    optimistic fallback just keeps writeable UI buttons enabled.
    if (!role) return false;
    return ROLE_FALLBACK[role]?.[domain]?.[act] === true;
  };

  const mayView = (action: string): boolean => {
    const sep = action.indexOf(':');
    if (sep === -1) return false;
    const domain = action.slice(0, sep);
    const act = action.slice(sep + 1);
    const capabilities = centralCtx?.currentWorkspace?.capabilities;
    // Absent data → allow (menus stay visible). Explicit false → hide.
    if (!capabilities) return true;
    const appCaps = capabilities[APP_ID];
    if (!appCaps) return true;
    const domainCaps = appCaps[domain];
    if (!domainCaps) return true;
    if (!(act in domainCaps)) return true;
    return domainCaps[act] === true;
  };

  const hasRole = (...roles: Role[]): boolean => {
    if (!centralCtx) return false;
    return sharedHasRole(centralCtx, roles);
  };

  const isAtLeast = (minRole: Role): boolean => {
    if (!centralCtx) return false;
    return hasMinimumRole(centralCtx, minRole);
  };

  const hasCapability = (domain: string, action: string): boolean => {
    if (!centralCtx) return false;
    return sharedHasCapability(centralCtx, APP_ID, domain, action);
  };

  const hasWorkspacePermission = (permission: WorkspacePermissionKey): boolean => {
    if (!centralCtx) return false;
    return sharedHasWorkspacePermission(centralCtx, permission);
  };

  return {
    role,
    can,
    mayView,
    hasRole,
    isAtLeast,
    hasCapability,
    hasWorkspacePermission,
    isViewer: role === 'viewer',
    isAdmin: role === 'admin' || role === 'owner',
    isOwner: role === 'owner',
  };
}

/**
 * Wrapper component that conditionally renders based on permission.
 */
export function RequirePermission({
  action,
  children,
  fallback = null,
}: {
  action: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { can } = usePermissions();
  return can(action) ? <>{children}</> : <>{fallback}</>;
}

/**
 * Gets tooltip text for disabled actions based on role.
 */
export function getPermissionTooltip(role: Role | undefined): string {
  switch (role) {
    case 'viewer':
      return 'You have view-only access. Contact your workspace admin for write access.';
    case 'member':
      return 'This action requires admin or owner permissions.';
    case 'admin':
      return 'This action requires owner permissions.';
    default:
      return 'Insufficient permissions';
  }
}
